<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" persistent max-width="1000" scrollable content-class="round-2xl")
  v-card
    v-card-title.px-4
      v-row(no-gutters)
        v-col(cols="10" offset="1")
          .text-xl.font-bold.text-center {{ vitalSign ? 'Edit vital' : 'Record vital' }}
        v-col(cols="1" align="right")
          v-btn(icon @click="closeDialog" small color="primary")
            v-icon mdi-close
    v-divider
    v-card-text.pa-6(outlined)
      v-row
        v-col(cols="3")
          div.text-sm.font-semibold.neutral500--text Date
          y-dialog(v-model='showPicker' width='380px' persistent content-class="round-3xl")
            template(v-slot:activator='{ on }')
              v-text-field.text-sm.mt-2.round-xl(v-model="sign.field_date" placeholder="DD/MM/YYYY" outlined dense height="48" :rules="rules.not_empty" hide-details="auto" v-on="on" readonly)
                template(v-slot:append)
                  eva-icon(name="calendar-outline" fill="#848484" style="margin-top: 2px;")
            v-card
              v-card-text.px-2.pt-2.pb-0
                yc-date-picker(:parentDate.sync="sign.date" color="info" @change="pickDate(sign)")
        v-col(cols="3")
          div.text-sm.font-semibold.neutral500--text Time
          v-text-field.text-sm.mt-2.round-xl(v-model="sign.time" outlined dense height="48" :rules="rules.not_empty" type="time" hide-details="auto")
      
      v-row
        v-col(cols="3")
          div.text-sm.font-semibold.neutral500--text Height
          v-text-field.text-sm.mt-2.round-xl(v-model="sign.height" placeholder="0" outlined dense height="48" suffix="cm" type="number" hide-details="auto")
        v-col(cols="3")
          div.text-sm.font-semibold.neutral500--text Weight
          v-text-field.text-sm.mt-2.round-xl(v-model="sign.weight" placeholder="0" outlined dense height="48" suffix="kg" type="number" hide-details="auto")
        v-col(cols="3")
          div.text-sm.font-semibold.neutral500--text Blood pressure
          v-text-field.text-sm.mt-2.round-xl(v-model="sign.blood_pressure" placeholder="0/0" outlined dense height="48" :rules="sign.blood_pressure ? rules.valid_blood_pressure : []" suffix="mmHg" hide-details="auto")
        v-col(cols="3")
          div.text-sm.font-semibold.neutral500--text Pulse
          v-text-field.text-sm.mt-2.round-xl(v-model="sign.pulse" placeholder="0" outlined dense height="48" suffix="bpm" type="number" hide-details="auto")
      
      v-row
        v-col(cols="3")
          div.text-sm.font-semibold.neutral500--text Temperature
          v-text-field.text-sm.mt-2.round-xl(v-model="sign.temperature" placeholder="0" outlined dense height="48" suffix="°C" type="number" hide-details="auto")
        v-col(cols="3")
          div.text-sm.font-semibold.neutral500--text Blood glucose
          v-text-field.text-sm.mt-2.round-xl(v-model="sign.blood_glucose" placeholder="0" outlined dense height="48" suffix="mmol/L" type="number" hide-details="auto")
        v-col(cols="3")
          div.text-sm.font-semibold.neutral500--text SpO2
          v-text-field.text-sm.mt-2.round-xl(v-model="sign.spo2" placeholder="0" outlined dense height="48" suffix="%" type="number" hide-details="auto")
        v-col(cols="3")
          div.text-sm.font-semibold.neutral500--text Respiratory rate
          v-text-field.text-sm.mt-2.round-xl(v-model="sign.respiratory_rate" placeholder="0" outlined dense height="48" suffix="breath per min" type="number" hide-details="auto")

    v-divider
    v-card-actions.px-6.white
      v-spacer
      y-button(:label="$t('cancel')" underline color="neutral700" size="sm" button-class="px-0" @click="closeDialog")
      y-button.ml-3(label="Save" size="sm" :loading="isLoading" @click="saveData")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { DateTime } from 'luxon'

export default {
  components: {},
  props: {
    open: {
      type: Boolean,
    },
    vitalSign: {
      type: Object,
      default: null
    },
  },
  data () {
    return {
      isFetching: false,
      isLoading: false,
      showPicker: false,
      showTablePicker: false,
      selectedDateField: null,
      selectedDate: null,
      sign: {},
      signs: [],
      chartSigns: [],
      chartCategories: [],
      headers: [
        { text: 'Date', value: 'date', width: 170 },
        { text: 'Time', value: 'time', width: 170 },
        { text: 'Height', value: 'height' },
        { text: 'Weight', value: 'weight' },
        { text: 'Temp', value: 'temperature', width: 120 },
        { text: 'BP', value: 'blood_pressure', width: 120 },
        { text: 'Pulse', value: 'pulse' },
        { text: 'SpO2', value: 'spo2' },
        { text: 'BGL', value: 'blood_glucose' },
        { text: 'Resp rate', value: 'respiratory_rate' },
        { text: 'Actions', value: 'actions' },
      ],
      rules: {
        not_empty: [v => (v != null && v.toString().trim().length) || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 1|| `Amount should be more than ${this.$currency.symbol} 1`],
        not_empty_select: [v => !!v || 'This field is required'],
        valid_blood_pressure: [ v => /^\d+\/\d+$/.test(v) || 'Blood pressure must be valid', ]
      },
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      pid: 'treatment/getLeadPid',
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (newValue) {
        this.$emit('update:open', newValue)
      }
    },
  },
  watch: {
  },
  mounted () {
    this.setData()
  },
  methods: {
    ...mapActions(['showMessage']),
    setData () {
      if (this.vitalSign) {
        const parsedDatetime  = DateTime.fromISO(this.vitalSign.recorded_at)
        const date = parsedDatetime.toFormat('yyyy-MM-dd')
        const time = parsedDatetime.toFormat('HH:mm')
        const field_date = this.$options.filters.displayDate(date)
        let blood_pressure = null
        if (this.vitalSign.bp_systolic && this.vitalSign.bp_diastolic) {
          blood_pressure = `${this.vitalSign.bp_systolic}/${this.vitalSign.bp_diastolic}`
        }
        this.sign = { ...this.vitalSign, date, time, field_date, blood_pressure, editable: false }
      } else {
        this.updateCurrentDateTime()
      }
    },
    pickDate (sign) {
      sign.field_date = this.$options.filters.displayDate(sign.date)
      this.showPicker = false
    },
    updateCurrentDateTime () {
      const now = DateTime.local()
      let date = now.toFormat('yyyy-MM-dd')
      let time = now.toFormat('HH:mm')
      this.$set(this.sign, 'field_date', this.$options.filters.displayDate(date))
      this.$set(this.sign, 'date', date)
      this.$set(this.sign, 'time', time)
    },
    saveData () {
      this.isLoading = true
      let data = { ...this.sign }
      data.recorded_at = `${this.sign.date} ${this.sign.time}`
      if (this.sign.blood_pressure) {
        data.bp_systolic = this.sign.blood_pressure.split('/')[0]
        data.bp_diastolic = this.sign.blood_pressure.split('/')[1]
      } else {
        data.bp_systolic = null
        data.bp_diastolic = null
      }
      data = this.sanitizeEmpty(data)
      if (this.vitalSign) {
        this.$axios.patch(`/clinics/${this.company.slug}/consultations/${this.pid}/vital_signs/${this.vitalSign.id}`, data)
          .then(resp => {
            this.$emit('updated', resp.data )
            this.closeDialog()
          })
          .catch(err => {
            let message = this.$t('error_message.unable_to_complete_request')
            if (err.response.data) {
              if (err.response.data.detail) {
                message = err.response.data.detail
              } else {
                let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
                if (data) { message = data }
              }
            }
            this.showMessage(['error', message])
          })
          .finally(() => {
            this.isLoading = false
          })
      } else {
        this.$axios.post(`/clinics/${this.company.slug}/consultations/${this.pid}/vital_signs`, data)
          .then(resp => {
            this.$emit('updated', resp.data)
            this.closeDialog()
          })
          .catch(err => {
            let message = this.$t('error_message.unable_to_complete_request')
            if (err.response.data) {
              if (err.response.data.detail) {
                message = err.response.data.detail
              } else {
                let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
                if (data) { message = data }
              }
            }
            this.showMessage(['error', message])
          })
          .finally(() => {
            this.isLoading = false
          })
      }
    },
    sanitizeEmpty(data) {
      Object.keys(data).forEach(key => {
        if (data[key] === '' || data[key] === undefined || data[key] === null) {
          data[key] = null
        }
      })
      return data
    },
    closeDialog () {
      this.$emit('closed')
      this.showDialog = false
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep {
  .custom-style {
    .v-data-table__wrapper {
      table {
        .v-data-table-header {
          tr {
            background-color: var(--v-neutral300);
          }
        }
      }
    }
  }
}
</style>